import {
  UseMutationOptions,
  UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"
import { IEvent, ISession, IUnsubscribeFromEvents } from "./types"
import api from "services/api"

export function useMemberEvents(
  memberId: string,
  queryParams: { session_id: string },
  config?: UseQueryOptions<IEvent[]>
) {
  const params = {
    filter: {
      session: {
        operator: "in",
        values: queryParams.session_id,
      },
    },
  }
  return useQuery<IEvent[]>(
    [
      "operation",
      "administration",
      "members",
      "portal",
      memberId,
      "events",
      { params },
    ],
    {
      keepPreviousData: true,
      ...config,
    }
  )
}

export function useSessions(config?: UseQueryOptions<ISession[]>) {
  return useQuery<ISession[]>(
    ["operation", "administration", "members", "portal", "sessions"],
    config
  )
}

export function useCurrentSession(config?: UseQueryOptions<ISession>) {
  return useQuery<ISession>(
    ["operation", "administration", "members", "portal", "session"],
    config
  )
}

export function useMemberEventsFilters() {
  return useQuery([
    "operation",
    "administration",
    "members",
    "portal",
    "filters",
  ])
}

export function useEvent(eventId: string, config?: UseQueryOptions<IEvent>) {
  return useQuery<IEvent>(["operation", "production", "events", eventId], {
    ...config,
  })
}

export function useUnsubscribeFromEvents(
  memberId: string,
  config?: UseMutationOptions<unknown, unknown, IUnsubscribeFromEvents>
) {
  const queryClient = useQueryClient()
  return useMutation<unknown, unknown, IUnsubscribeFromEvents>(
    (data) =>
      api.post(
        `operation/administration/members/${memberId}/events/unsubscribe`,
        data
      ),
    {
      ...config,
      onSuccess(data, vars, context) {
        config?.onSuccess?.(data, vars, context)
        queryClient.invalidateQueries([
          "operation",
          "administration",
          "members",
          memberId,
          "events",
        ])
      },
    }
  )
}
